import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useState } from 'react'
import { type MRT_ExpandedState } from 'material-react-table'
import type {
    DocumentQueueTasks as DocumentQueueTaskKind,
    RegularQueueTaskRequestBody,
} from '@visorpro/client'

import {
    QueueTaskDetailPanel,
    QueueTasksTable,
} from '../../../../components/tasks-table'
import { useQueueTasks } from '../../../../stores/queue-tasks-store'
import { type Stores } from '../../../../util/store'
import { useParams } from 'react-router-dom'
import { useInterval } from 'react-use'

export interface QueueTasksPageProps {
    stores: Stores
}

const INTERVAL_DELAY = 5000

export const DocumentQueueTasks = observer(
    ({ stores }: QueueTasksPageProps) => {
        const [pagination, setPagination] = useState({})
        const [filters, setFilters] = useState({})
        const [expanded, setExpanded] = useState<MRT_ExpandedState>({})

        const { documentId } = useParams()

        if (!documentId) {
            return null
        }

        const options = useMemo(() => {
            return {
                metadata: { document_id: documentId },
                ...pagination,
                ...filters,
            }
        }, [documentId, pagination, filters])

        const tasksStore = useMemo(
            () => stores.documents.documentQueueTasksStoresById[documentId],
            [documentId, stores.documents],
        )
        const tasks = useQueueTasks(tasksStore, options)

        const createDocumentQueueTask = async (
            kind: DocumentQueueTaskKind | DocumentQueueTaskKind[],
        ) => {
            const toRequestBody = (kind: DocumentQueueTaskKind) =>
                ({
                    kind,
                    metadata: { document_id: documentId },
                }) as RegularQueueTaskRequestBody

            if (Array.isArray(kind)) {
                await tasksStore.create({ chain: kind.map(toRequestBody) })
            } else {
                await tasksStore.create(toRequestBody(kind))
            }
            await tasksStore.list(options)
        }

        useInterval(() => {
            if (!tasksStore.isFetching) {
                void tasksStore.list(options)
            }
        }, INTERVAL_DELAY)

        const renderDetailPanel = useCallback(
            (taskId: string) => {
                if (expanded === true || !expanded[taskId]) {
                    return null
                }

                return (
                    <QueueTaskDetailPanel store={tasksStore} taskId={taskId} />
                )
            },
            [expanded, tasksStore],
        )

        return (
            <QueueTasksTable
                tasks={tasks}
                rowCount={tasksStore.totalTasks}
                isLoading={tasksStore.isFetching && !tasksStore.isFetched}
                isCreating={tasksStore.isCreating}
                pagination={pagination}
                setPagination={setPagination}
                filters={filters}
                setFilters={setFilters}
                expanded={expanded}
                onExpandedChange={setExpanded}
                renderDetailPanel={renderDetailPanel}
                createDocumentQueueTask={createDocumentQueueTask}
            />
        )
    },
)
